import { Check, Clock, Link2 } from "lucide-react";
import type { ReactNode } from "react";

import { LinkScreen, PageScreen, TodayScreen } from "@/features/platform/components/app-screens";
import { ScheduleDesk } from "@/features/platform/components/desks";
import { Laptop, Phone } from "@/features/platform/components/devices";
import { Accent, displayTitle, Eyebrow, GoLink, Section } from "@/features/platform/components/landing-ui";
import { FloatChip, Stage } from "@/features/platform/components/stage";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

/** A phone standing in a wide tile, whole, with a chip floating beside it. */
const StandingPhone = ({ children, chip }: { children: ReactNode; chip?: ReactNode }) => (
  <div className="relative w-[min(290px,78%)] lg:w-[310px]">
    <Phone>{children}</Phone>
    {chip}
  </div>
);

const phoneArt = "flex justify-center px-6 pb-10 lg:items-center lg:py-14";

const STEPS = [
  {
    id: "account",
    short: () => m.start_page_rail_account(),
    title: () => m.start_page_account_title(),
    lead: () => m.platform_step_account_title(),
    body: () => m.start_page_account_body(),
    art: phoneArt,
    picture: () => (
      <StandingPhone
        chip={
          <FloatChip className="top-[22%] -left-[30%] max-lg:hidden">
            <Clock className="size-4 text-primary" />
            {m.start_page_account_chip()}
          </FloatChip>
        }
      >
        <PageScreen />
      </StandingPhone>
    ),
  },
  {
    id: "hours",
    short: () => m.start_page_rail_hours(),
    title: () => m.start_page_hours_title(),
    lead: () => m.platform_step_hours_title(),
    body: () => m.start_page_hours_body(),
    art: "flex items-center justify-center px-5 pt-2 pb-12 lg:py-16 lg:pr-0 lg:pl-12",
    picture: () => (
      <div className="w-full max-w-[760px]">
        <Laptop>
          <ScheduleDesk />
        </Laptop>
      </div>
    ),
  },
  {
    id: "share",
    short: () => m.start_page_rail_share(),
    title: () => m.start_page_share_title(),
    lead: () => m.platform_step_share_title(),
    body: () => m.start_page_share_body(),
    art: phoneArt,
    picture: () => (
      <StandingPhone
        chip={
          <FloatChip className="top-[46%] -right-[30%] max-lg:hidden">
            <Link2 className="size-4 text-primary" />
            {m.start_page_share_chip()}
          </FloatChip>
        }
      >
        <LinkScreen />
      </StandingPhone>
    ),
  },
  {
    id: "first",
    short: () => m.start_page_rail_first(),
    title: () => m.start_page_first_title(),
    lead: () => m.start_page_first_lead(),
    body: () => m.start_page_first_body(),
    art: phoneArt,
    picture: () => (
      <StandingPhone
        chip={
          <FloatChip className="top-[34%] -left-[30%] max-lg:hidden">
            <Check className="size-4 text-primary" strokeWidth={3} />
            {m.features_hero_chip_booked({ name: m.features_sample_client_first(), time: "18:30" })}
          </FloatChip>
        }
      >
        <TodayScreen />
      </StandingPhone>
    ),
  },
];

const stepNumber = (index: number) => String(index + 1).padStart(2, "0");

/**
 * The words that open `/how-it-works`, inside the head card, and the way in as a rail of
 * numbered stops, each a jump to its own tile below.
 */
export const StartHero = () => (
  <div className="relative mx-auto max-w-[920px] px-1 pt-14 pb-2 text-center md:pt-20">
    <Eyebrow>{m.platform_nav_how()}</Eyebrow>
    <h1 className={cn(displayTitle, "mt-6")}>
      {m.start_page_title_lead()} <Accent>{m.start_page_title_tail()}</Accent>
    </h1>
    <p className="mx-auto mt-6 max-w-[54ch] text-lg leading-[1.55] text-pretty text-muted-foreground">
      {m.start_page_lede()}
    </p>
    <div className="mt-8 flex flex-wrap justify-center gap-3">
      <GoLink to="/signup" tone="accent">
        {m.platform_nav_start()}
      </GoLink>
    </div>
    <ol className="mx-auto mt-14 flex max-w-[760px] flex-wrap justify-center gap-2 md:mt-16">
      {STEPS.map((step, index) => (
        <li key={step.id}>
          <a
            href={`#step-${step.id}`}
            className="group inline-flex h-11 items-center gap-2.5 rounded-full border border-border bg-background/70 pr-4 pl-1.5 text-sm font-medium backdrop-blur-xl transition-colors hover:bg-muted"
          >
            <span className="grid size-8 place-items-center rounded-full bg-primary text-[12px] font-semibold text-primary-foreground tabular-nums transition-transform duration-200 group-hover:scale-105">
              {stepNumber(index)}
            </span>
            {step.short()}
          </a>
        </li>
      ))}
    </ol>
  </div>
);

/** Each stop of the way in, one wide tile apiece, the pictures zigzagging left and right. */
export const StartSteps = () => (
  <Section>
    <div className="grid gap-3">
      {STEPS.map((step, index) => (
        <Stage
          key={step.id}
          id={`step-${step.id}`}
          side
          flip={index % 2 === 1}
          eyebrow={m.start_page_step({ number: stepNumber(index) })}
          title={step.title()}
          lead={step.lead()}
          body={step.body()}
          className="lg:min-h-[640px]"
          artClassName={step.art}
        >
          {step.picture()}
        </Stage>
      ))}
    </div>
  </Section>
);
